import {
  createElement,
  FC,
  MutableRefObject,
  useEffect,
  useRef,
  BaseSyntheticEvent,
  Fragment,
  useState,
  useCallback,
} from 'react';
import {Controller, UseFormReturn} from 'react-hook-form';
import {Box, SxProps, Theme} from '@mui/material';
import {CarePlanConfigurationLayout} from '../../backend/interfaces/care-plan-configuration-layout';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../backend/requests/care-plan-activities-group';
import {ConfigurationsIndex, ConfigurationsValuesIndex} from '../../types';
import {CarePlanConfigurationType} from '../../backend/enums/care-plan-configuration-type';
import {CarePlanConfigurationFeature} from '../../backend/enums/care-plan-configuration-feature';
import {getFieldInitialControlledRenderValue, mapDefaultValues, mapPrefilledValues} from './values';
import {getFieldRenderComponent} from './components';
import {getFieldTypeProps, getFieldValidations} from './props';
import {DynamicFormManager} from '../../managers/dynamic-form';

// NOTE: Just leaving this here so however needs to debug, knows that we can make use of this tool
// import { DevTool } from '@hookform/devtools';

export type DynamicFormFieldShape = any;

interface DynamicFormFieldsProps {
  id: string;
  activityGroupId?: string;
  activityTypeId?: string;
  activitiesSectorId?: string;
  formInstance: UseFormReturn<DynamicFormFieldShape>;
  configurationsIndex: ConfigurationsIndex;
  layout?: CarePlanConfigurationLayout;
  defaultValuesIndex?: ConfigurationsValuesIndex;
  availableSpace?: string;
  configuredRepetition?: 'activities-group' | 'activity-type';
  onSubmit: (data: Array<CarePlanActivitiesGroupApiRequestConfigurationsItem>) => void;
  readonly?: boolean;
}

export const DynamicForm = ({
  id,
  formInstance,
  configurationsIndex,
  layout,
  onSubmit,
  availableSpace = '540px',
  defaultValuesIndex,
  configuredRepetition,
  activityGroupId,
  activityTypeId,
  activitiesSectorId,
  readonly: readOnly,
}: DynamicFormFieldsProps) => {
  const availableSpaceRef = useRef() as MutableRefObject<HTMLDivElement>;

  const containerWidth = availableSpaceRef.current?.getBoundingClientRect().width;

  const [space, setSpace] = useState<string | undefined>();

  const calcAvailableSpace = useCallback(
    () => (containerWidth ? `${containerWidth}px` : availableSpace),
    [availableSpace, containerWidth]
  );

  const availableCalculationSpacerSx: SxProps<Theme> = {
    boxSizing: 'border-box',
    display: 'block',
    height: '1px',
    width: '100%',
    position: 'relative',
  };

  const formWrapperSx: SxProps<Theme> = {
    display: 'table-row',
    position: 'relative',
    width: '-webkit-fill-available',
    overflow: 'hidden',
  };

  const formSx: SxProps<Theme> = {
    position: 'relative',
    maxWidth: '100%',
    display: 'grid',
    gap: '30px 10px',
    gridTemplateColumns: `repeat(${layout?.desktopColumnCount}, calc((${space} - ${
      (layout?.desktopColumnCount ?? 1) - 1
    } * 10px) / ${layout?.desktopColumnCount}))`,
  };

  // Enqueue form instance
  const {control, handleSubmit, reset} = formInstance;

  // On submit callback
  const onSubmitTrigger = (data: DynamicFormFieldShape, e?: BaseSyntheticEvent) => {
    e?.preventDefault();

    if (!configurationsIndex) return;

    const configurations = DynamicFormManager.instance.remapCarePlanConfigurationValue(
      configurationsIndex,
      data
    );

    onSubmit(configurations);
  };

  // Set fields values on mount
  useEffect(() => {
    if (defaultValuesIndex) {
      const values = mapPrefilledValues(configurationsIndex, defaultValuesIndex);
      reset(values);
    } else {
      const defaultValues = mapDefaultValues(configurationsIndex, layout);
      reset(defaultValues);
    }
  }, [configurationsIndex, defaultValuesIndex, formInstance, layout, reset]);

  // Set form grid available space
  useEffect(() => {
    setSpace(calcAvailableSpace);
  }, [calcAvailableSpace, containerWidth]);

  return (
    <>
      {/* NOTE: Just leaving this here so however needs to debug, knows that we can make use of this tool */}
      {/* <DevTool control={control} /> */}
      <Box sx={availableCalculationSpacerSx} ref={availableSpaceRef} />
      <Box sx={formWrapperSx}>
        <Box component="form" id={id} onSubmit={handleSubmit(onSubmitTrigger)} sx={formSx}>
          {configurationsIndex &&
            layout?.configurations?.map((fieldLayout) => {
              // Get field
              const field = configurationsIndex[fieldLayout.carePlanConfigurationId];

              const required = fieldLayout.required || false;

              // Field react node unique ID
              const reactKey = `${id}-${fieldLayout.carePlanConfigurationId}`;

              // If no match - early return ⏭️
              if (!field) return <Fragment key={reactKey} />;

              // Get field component
              const component: FC<{
                value: any;
                errors?: string;
              }> = getFieldRenderComponent(field);

              // Assemble field appliable validation rules
              const rules = getFieldValidations(field, required, configuredRepetition);

              // Assemble required props per field
              const props = getFieldTypeProps(
                field,
                required,
                configurationsIndex,
                // A context around the form could aid us here.
                {
                  formInstance,
                  activityGroupId: activityGroupId!,
                  activityTypeId: activityTypeId!,
                  activitiesSectorId: activitiesSectorId!,
                  carePlanConfigurationId: field.id!,
                  configuredRepetition,
                  editMode: readOnly,
                }
              );

              // If no component handed over - return fragment
              if (!component) return <Fragment key={reactKey} />;

              // Escape field `start-at-date` in case of opening activity type modal when the group activity repetition is defined per group
              if (
                field.feature === CarePlanConfigurationFeature.StartsAtDate &&
                configuredRepetition === 'activities-group'
              )
                return <Fragment key={reactKey} />;

              // Medication schedule - Escape fields for `momento da toma` and `dosagem`
              if (
                (field.feature === CarePlanConfigurationFeature.MedicineDosage ||
                  field.feature === CarePlanConfigurationFeature.MedicineTimeOfTaking) &&
                configuredRepetition === 'activity-type'
              )
                return <Fragment key={reactKey} />;

              // Return field
              return (
                <Box
                  key={reactKey}
                  sx={{
                    width: '100%',
                    gap: '20px',
                    placeItems: 'start',
                    order: fieldLayout.displayOrder,
                    gridColumn: `span ${fieldLayout.columnsSpan ?? 1}`,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {field.type === CarePlanConfigurationType.EmptySpace ? (
                    <Box
                      key={reactKey}
                      sx={{
                        width: '100%',
                        gap: '20px',
                        placeItems: 'start',
                        order: fieldLayout.displayOrder,
                        gridColumn: `span ${fieldLayout.columnsSpan ?? 1}`,
                      }}
                    />
                  ) : (
                    <Controller
                      name={field.id!}
                      control={control}
                      rules={rules}
                      render={({field: fieldProps, fieldState: {error}}) => {
                        let firstRenderValue = getFieldInitialControlledRenderValue(field);

                        return createElement(component, {
                          ...props,
                          ...fieldProps,
                          value: fieldProps.value ?? firstRenderValue,
                          errors: error?.message,
                        });
                      }}
                    />
                  )}
                </Box>
              );
            })}
        </Box>
      </Box>
    </>
  );
};
